import { ArrowCcwIcon, ArrowDownIcon, ArrowUpIcon, FilterIcon } from "@xola/icons";
import { Alert, Spinner, SubmitButton } from "@xola/ui-kit";
import { ComponentStateChangedEvent, DomLayoutType, FirstDataRenderedEvent, IHeaderParams } from "ag-grid-community";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import {
    ColDef,
    Column,
    GetRowIdParams,
    LicenseManager,
    NumberFilterModel,
    PostProcessPopupParams,
    SetFilterModel,
    TextFilterModel,
} from "ag-grid-enterprise";
import { AgGridReact, AgGridReactProps } from "ag-grid-react";
import clsx from "clsx";
import { isNil, map, mapValues, omit, omitBy } from "lodash";
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { Report, ReportObject } from "types";
import { ErrorBoundary } from "../../ErrorBoundary";
import { columnTypes } from "./ColumnTypes";
import { ReportFilter, ReportFilterOperator, ReportFilterOperatorCopy, ReportFilterState } from "./ReportFilterState";
import { ReportColumnExpandDef, ReportColumnFilterValueType } from "./SuggestedReports";
import { config } from "@/config";
import { useSeller } from "@/hooks/useAccount";
import "./CustomReportGrid.css";

export interface CustomReportGridRef {
    resetColumnFilter: (id: keyof ReportFilterState) => void;
}

interface CustomReportGridProps extends AgGridReactProps {
    height: number;
    values: any;
    count?: number;
    hasMore?: boolean;
    isLoading?: boolean;
    isLoadingMore?: boolean;
    defaultFilter: ReportFilterState;
    filter: ReportFilterState;
    report: Report;
    onFilterChange: (filter: ReportFilterState) => void;
    onLoadMore?: () => void;
    onRowClicked?: (rowData: any) => void;
}

interface GridColumnDef extends ColDef {
    expand?: ReportColumnExpandDef;
    parent?: { field: string; headerName: string };
    filterValueType?: ReportColumnFilterValueType;
}

const sanitizeColumnDef = (columnDef: GridColumnDef): ColDef => {
    return omit(columnDef, ["expand", "parent", "filterValueType"]);
};

export const CustomReportGrid = forwardRef<CustomReportGridRef, CustomReportGridProps>(
    (
        {
            height,
            values,
            count,
            filter,
            hasMore,
            isLoading,
            isLoadingMore,
            onFilterChange,
            onLoadMore,
            columnDefs,
            onRowClicked,
            defaultFilter,
            report,
            ...rest
        },
        ref,
    ) => {
        const gridRef = useRef<AgGridReact>(null);
        const seller = useSeller();

        const handleFilterChanged = useCallback(() => {
            if (gridRef.current?.api) {
                const filterModel: Record<string, FilterModel> = gridRef.current.api.getFilterModel();
                const formattedFilterModel = getFormattedFilterModel(filterModel, filter, defaultFilter, report);
                onFilterChange(getReportFilterState(formattedFilterModel));
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [filter, defaultFilter]);

        // Reset a specific column filter using setColumnFilterModel
        const resetColumnFilter = useCallback((columnId: keyof ReportFilterState) => {
            if (gridRef.current?.api) {
                gridRef.current.api.setColumnFilterModel(columnId as string, null);
                // Refresh data after filter reset
                gridRef.current.api.onFilterChanged();
            }
        }, []);

        // Expose reset functions to parent to reset filters
        useImperativeHandle(ref, () => ({
            resetColumnFilter,
        }));

        const resizeColumns = useCallback(
            (colId: string) => {
                gridRef.current?.api.autoSizeColumns([colId]);
            },
            [gridRef],
        );

        const resizeAllColumns = useCallback((params: FirstDataRenderedEvent | ComponentStateChangedEvent) => {
            const autoSizableColIds = params.api
                .getColumns()
                ?.filter((column) => !column.getColDef().suppressAutoSize)
                .map((column) => column.getColId());

            if (autoSizableColIds?.length) {
                params.api.autoSizeColumns(autoSizableColIds);
            }
        }, []);

        const handlePostProcessPopup = useCallback(
            (params: PostProcessPopupParams) => {
                const colId = params.column?.getColId();
                // suppressAutoSize means the column intentionally keeps its flex/minWidth sizing (e.g. Product,
                // SKU) — auto-sizing it to fit a long value when its filter popup opens would expand the whole
                // grid horizontally past the viewport.
                if (colId && !params.column?.getColDef().suppressAutoSize) {
                    resizeColumns(colId);
                }
            },
            // eslint-disable-next-line react-hooks/exhaustive-deps
            [filter, defaultFilter],
        );

        // Portal all the column filters into .ag-theme-alpine outside of the header section so they don't get
        // clipped by the header
        const popupParent = useMemo<HTMLElement>(() => document.querySelector(".ui-column-layout-fill")!, []);

        // Hack to protect ES showing mismatching totals
        const currentCount = hasMore ? values.length : count;
        const [domLayout, setDomLayout] = useState<DomLayoutType>("normal");
        const sanitizedColumnDefs = useMemo(() => {
            if (!Array.isArray(columnDefs)) {
                return columnDefs;
            }

            return columnDefs.map((columnDef) => sanitizeColumnDef(columnDef as GridColumnDef));
        }, [columnDefs]);

        useEffect(() => {
            const handleBeforePrint = () => {
                if (gridRef.current?.api) {
                    setDomLayout("print");
                }
            };

            const handleAfterPrint = () => {
                if (gridRef.current?.api) {
                    setDomLayout("normal");
                }
            };

            window.addEventListener("beforeprint", handleBeforePrint);
            window.addEventListener("afterprint", handleAfterPrint);

            return () => {
                window.removeEventListener("beforeprint", handleBeforePrint);
                window.removeEventListener("afterprint", handleAfterPrint);
            };
        }, []);

        return (
            <div>
                <ErrorBoundary>
                    <div
                        className="ag-theme-alpine ui-report-grid-table"
                        style={{ width: "100%", height: getGridHeight(height) }}
                    >
                        <AgGridReact
                            key={JSON.stringify(filter)}
                            ref={gridRef}
                            enableCellTextSelection
                            ensureDomOrder
                            suppressContextMenu
                            suppressRowClickSelection
                            serverSideOnlyRefreshFilteredGroups
                            theme="legacy"
                            defaultColDef={defaultColDef}
                            domLayout={domLayout}
                            columnDefs={sanitizedColumnDefs}
                            components={components}
                            columnTypes={columnTypes}
                            context={{ seller }}
                            rowData={values}
                            loading={isLoading}
                            loadingOverlayComponent={DataOverlay}
                            loadingOverlayComponentParams={{ text: "Loading..." }}
                            noRowsOverlayComponent={DataOverlay}
                            noRowsOverlayComponentParams={{ text: "No Data" }}
                            getRowId={getRowId}
                            popupParent={popupParent}
                            postProcessPopup={handlePostProcessPopup}
                            onFirstDataRendered={resizeAllColumns}
                            onComponentStateChanged={resizeAllColumns}
                            onFilterChanged={handleFilterChanged}
                            onRowClicked={onRowClicked}
                            {...rest}
                        />
                    </div>
                </ErrorBoundary>

                <div className="mt-4 flex h-8 items-center justify-between">
                    {/* Dummy div so that the other sections are aligned in the middle and at the end */}
                    <div />
                    {hasMore ? (
                        <SubmitButton
                            isLoading={isLoadingMore}
                            icon={<ArrowCcwIcon />}
                            color="secondary"
                            variant="outline"
                            onClick={onLoadMore}
                        >
                            Load More
                        </SubmitButton>
                    ) : null}

                    {currentCount > 0 && (
                        <div className="text-base text-secondary-darker">
                            Showing{" "}
                            {isLoading ? (
                                <Spinner size="tiny" />
                            ) : (
                                <var data-var="currentCount">{currentCount ?? 0}</var>
                            )}{" "}
                            {isNil(count) ? null : (
                                <>
                                    &nbsp;of <var data-var="totalCount">{count}</var>
                                </>
                            )}
                            &nbsp;records
                        </div>
                    )}
                </div>
            </div>
        );
    },
);

const disableFilterSwitch = new Set([ReportObject.ABANDONS, ReportObject.PURCHASES]);

export const getFormattedFilterModel = (
    filterModel: Record<string, FilterModel>,
    filter: ReportFilterState,
    defaultFilter: ReportFilterState,
    report: Report,
) => {
    const formattedNewFilterModel = { ...filterModel };
    const formattedInitFilterModel = { ...filter };
    const filterModelKeys = Object.keys(formattedNewFilterModel);
    const filterKeys = Object.keys(filter);
    const isFilterSwitchAllowed = !disableFilterSwitch.has(report?.object);

    // All date keys should be included in defaultFilterKeys, cos if we switch date field to another one from default, it will lead to incorrect filters setting from table
    let defaultFilterKeys = Object.keys(defaultFilter);
    const dateKeys = ["createdAt", "items.realizedAt", "arrivalDate"];
    const missingDateKeys = dateKeys.filter((key) => !defaultFilterKeys.includes(key));
    if (missingDateKeys.length > 0) {
        defaultFilterKeys = [...defaultFilterKeys, ...missingDateKeys];
    }

    if (filterModelKeys.includes("items.realizedAt")) {
        delete formattedNewFilterModel.createdAt;
    }

    // If filter includes arrivalDate, delete createdAt and replace it with arrivalDate
    if (
        isFilterSwitchAllowed &&
        filterKeys.includes("arrivalDate") &&
        !filterKeys.includes("rangeName") &&
        filterModelKeys.includes("createdAt")
    ) {
        delete formattedNewFilterModel.arrivalDate;
        delete formattedInitFilterModel.arrivalDate;
    }

    // If filter includes createdAt, delete arrivalDate and replace it with createdAt
    if (
        isFilterSwitchAllowed &&
        filterKeys.includes("createdAt") &&
        !filterKeys.includes("rangeName") &&
        filterModelKeys.includes("arrivalDate")
    ) {
        delete formattedNewFilterModel.createdAt;
        delete formattedInitFilterModel.createdAt;
    }

    const formattedFilterModel: Record<string, FilterModel> = {};

    // Merge formattedInitFilterModel and formattedNewFilterModel without 'undefined' values
    for (const key of new Set([...Object.keys(formattedInitFilterModel), ...Object.keys(formattedNewFilterModel)])) {
        if (formattedInitFilterModel[key] !== undefined || defaultFilterKeys.includes(key)) {
            formattedFilterModel[key] = formattedInitFilterModel[key] as FilterModel;
        }

        const skipReassignKeys = ["createdAt", "items.realizedAt"];
        const value = formattedFilterModel[key];
        const isSkipReassignKey =
            skipReassignKeys.includes(key) && value !== undefined && typeof value === "object" && "rangeName" in value;

        if (!isSkipReassignKey && formattedNewFilterModel[key] !== undefined) {
            formattedFilterModel[key] = formattedNewFilterModel[key];
        }
    }

    return formattedFilterModel;
};

// Convert `FilterModel` to `ReportFilterState`
export const getReportFilterState = (filter: Record<string, FilterModel>): ReportFilterState => {
    const reportFilterState = mapValues(filter, (filter: FilterModel): ReportFilter | undefined => {
        if ("operator" in filter) {
            // ReportFilter
            return filter;
        }

        if ("filterType" in filter) {
            if (filter.filterType === "set") {
                // SetFilterModel
                const value = filter.values.filter((value) => !isNil(value)) as string[];

                if (isBooleanFilter(filter)) {
                    // When boolean filter is true we use 'equals' operator, when it's false we use 'notEqual' operator to show false values and empty values
                    if (value[0] === "true") {
                        return { operator: ReportFilterOperator.EQ, value: true };
                    }
                    return { operator: ReportFilterOperator.NE, value: true };
                }

                if (value.length > 0) {
                    return { operator: ReportFilterOperator.IN, value };
                }
            }

            const isTextOrNumber = filter.filterType === "number" || filter.filterType === "text";

            if (isTextOrNumber && !isNil(filter.filter)) {
                return { operator: filter.type as ReportFilterOperator, value: filter.filter };
            }
        }

        // `undefined` values will be removed below
    });

    return omitBy(reportFilterState, (filter) => isNil(filter)) as ReportFilterState;
};

const getGridHeight = (availableHeight: number) => {
    const margin = 16; // mt-4
    const buttonHeight = 30; // Load more button height
    return availableHeight - buttonHeight - margin;
};

export interface CustomColumnHeaderProps extends IHeaderParams {
    column: Column;
    displayName: string;
}

export const CustomColumnHeader = ({
    column,
    displayName,
    api,
    enableSorting,
    progressSort,
}: CustomColumnHeaderProps) => {
    const refButton = useRef(null);
    const enableFilter = !!column.getColDef().filter;
    const headerClassName = column.getColDef().headerClass;
    const [sort, setSort] = useState<"asc" | "desc" | null>(column.getSort() ?? null);

    useEffect(() => {
        const onSortChanged = () => setSort(column.getSort() ?? null);
        column.addEventListener("sortChanged", onSortChanged);
        return () => column.removeEventListener("sortChanged", onSortChanged);
    }, [column]);

    const handleFilterClick = () => {
        if (refButton.current) {
            api.showColumnFilter(column);
        }
    };

    const handleSortClick = (event: React.MouseEvent) => {
        if (enableSorting) {
            progressSort(event.shiftKey);
        }
    };

    return (
        <div className={clsx("flex w-full space-x-2", headerClassName ?? "justify-between")}>
            <span
                className={clsx("flex items-center gap-1", enableSorting && "cursor-pointer")}
                onClick={handleSortClick}
            >
                {displayName.replace("Id", "ID")}
                {sort === "asc" && <ArrowUpIcon />}
                {sort === "desc" && <ArrowDownIcon />}
            </span>
            {enableFilter && (
                <span ref={refButton} className="cursor-pointer" onClick={handleFilterClick}>
                    <FilterIcon className="text-blue" />
                </span>
            )}
        </div>
    );
};

const components: Record<string, any> = {
    agColumnHeader: CustomColumnHeader,
};

const defaultColDef: ColDef = {
    flex: 1,
    resizable: true,
    minWidth: 150,
    sortable: false,
    filter: false,
    menuTabs: [],
    filterParams: {
        suppressAndOrCondition: true,
        buttons: ["apply"],
        closeOnApply: true,
        filterOptions: map(ReportFilterOperatorCopy, (label, value) => ({
            displayKey: value,
            displayName: label,
            predicate: () => true,
        })),
    },
};

const DataOverlay = ({ text, color = "secondary" }: { text: string; color: string }) => (
    <Alert color={color} className="inline-flex w-96 items-center p-4 text-md leading-5">
        {text}
    </Alert>
);

const getRowId = (params: GetRowIdParams) => {
    const { data } = params;

    // Merchandise variants share a top-level id; append lineItem.id so each variant gets a distinct row.
    if (data?.lineItem?.id) {
        const prefix = data._id ?? data.id ?? data.purchase?.id ?? data.product?.id;
        return prefix ? `${prefix}-${data.lineItem.id}` : data.lineItem.id;
    }

    if (!data?.product?.id && !data?._id && !data?.id && data?.items) {
        // Reservations by listing payload does not have 'id' attribute so we should use just product
        return data.items[0].product.id;
    }

    if (data.guest?.id) {
        return data.guest.id;
    }

    if (data.product?.id && data.arrivalDate && data.arrivalTime) {
        return `${data.id}-${data.product.id}-${data.id}-${data.arrivalDate}-${data.arrivalTime}`;
    }

    // _id is returned from redemption app response for Ticket redemption report
    return data._id ?? data.id;
};

type FilterModel = ReportFilter | TextFilterModel | NumberFilterModel | SetFilterModel;

if (config.agGrid?.key) {
    LicenseManager.setLicenseKey(config.agGrid.key);
}

const isBooleanFilter = (filter: SetFilterModel): boolean =>
    Array.isArray(filter.values) &&
    filter.values.length > 0 &&
    filter.values.every((v) => ["true", "false"].includes(String(v).toLowerCase()));
